// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useTooltip.tsx
 * @input Uses useLayer, React hooks
 * @output Exports useTooltip hook for hover/focus triggered tooltips
 * @position Layer hook; builds on useLayer for tooltip behavior
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Tooltip/index.ts
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useLayer } from "../Layer/useLayer.js";
import { layerAnimations } from "../Layer/layerAnimations.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import "../theme/tokens.stylex.js";
import { colorVars, radiusVars, spacingVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";

/**
 * Grace period (ms) before hiding on pointer-leave when no explicit `hideDelay`
 * is set, so the pointer can travel across the small gap from the trigger onto
 * the tooltip surface without the tooltip disappearing (WCAG 1.4.13 hoverable).
 */
import { jsx as _jsx } from "react/jsx-runtime";
const HOVER_BRIDGE_DELAY = 100;
const styles = {
  container: {
    kWkggS: "x19aspcf",
    kMwMTN: "xrkvqaz",
    kaIpWk: "x1hviunn",
    kMv6JI: "x9ynric",
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    $$css: true
  },
  marginBlock: {
    keoZOQ: "xcsaf9d",
    k1K539: "x14cgwvg",
    keTefX: "x1lziwak",
    k71WvV: "x14z9mp",
    $$css: true
  },
  marginInline: {
    keoZOQ: "xdj266r",
    k1K539: "xat24cr",
    keTefX: "x11g1kdw",
    k71WvV: "xnur1sd",
    $$css: true
  }
};

/**
 * Focus trigger behavior for tooltips
 */

/**
 * Check if an element is naturally focusable
 */
function isFocusable(element) {
  // Elements with explicit tabindex
  if (element.hasAttribute('tabindex')) {
    return element.tabIndex >= 0;
  }

  // Naturally focusable elements
  const focusableTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
  if (focusableTags.includes(element.tagName)) {
    return !element.disabled;
  }

  // Elements with contenteditable
  if (element.isContentEditable) {
    return true;
  }
  return false;
}

/**
 * Hook for tooltip behavior with hover/focus triggers.
 *
 * Builds on useLayer to add:
 * - Hover triggers with configurable delay
 * - Focus triggers with auto-detection for focusable elements
 * - Inverted color palette for high contrast
 *
 * Unlike HoverCard, tooltips:
 * - Don't stay open when hovering the tooltip content
 * - Have shorter delays
 * - Use inverted colors (dark background, light text)
 * - Are typically used for short, non-interactive text
 *
 * @example
 * ```
 * const tooltip = useTooltip({ placement: 'above' });
 * <Button ref={tooltip.ref} aria-describedby={tooltip.describedBy}>
 *   Hover me
 * </Button>
 * {tooltip.renderTooltip('Helpful tooltip text')}
 * ```
 */
export function useTooltip(options = {}) {
  const {
    placement = 'above',
    alignment = 'center',
    delay = 200,
    hideDelay = 0,
    focusTrigger = 'auto',
    isEnabled = true,
    isOpen,
    isDefaultOpen = false,
    onShow,
    onHide
  } = options;

  // Select margin style based on placement axis
  const marginStyle = placement === 'above' || placement === 'below' ? styles.marginBlock : styles.marginInline;
  const layer = useLayer({
    mode: 'context',
    onShow,
    onHide
  });
  const popoverXstyle = useMemo(() => [styles.container, marginStyle], [marginStyle]);
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const triggerRef = useRef(null);

  // Clear all timeouts
  const clearTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Schedule show with delay (suppressed when isOpen is false)
  const scheduleShow = useCallback(() => {
    if (!isEnabled || isOpen === false) {
      return;
    }
    clearTimeouts();
    showTimeoutRef.current = setTimeout(() => {
      layer.show();
    }, delay);
  }, [isEnabled, isOpen, clearTimeouts, layer, delay]);

  // Schedule hide with delay (suppressed when isOpen is true).
  // A small hover bridge (when hideDelay is 0) lets the pointer travel from the
  // trigger onto the tooltip surface without the tooltip vanishing — required
  // for WCAG 1.4.13 (Content on Hover or Focus: hoverable).
  const scheduleHide = useCallback(() => {
    if (isOpen === true) {
      return;
    }
    clearTimeouts();
    const effectiveHideDelay = hideDelay > 0 ? hideDelay : HOVER_BRIDGE_DELAY;
    hideTimeoutRef.current = setTimeout(() => {
      layer.hide();
    }, effectiveHideDelay);
  }, [isOpen, clearTimeouts, layer, hideDelay]);

  // Cancel a pending hide (e.g. the pointer entered the tooltip surface).
  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Event handlers
  const handleMouseEnter = useCallback(() => {
    // Suppress tooltips on touch devices — hover is simulated and eats a tap
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches) {
      return;
    }
    scheduleShow();
  }, [scheduleShow]);
  const handleMouseLeave = useCallback(() => {
    scheduleHide();
  }, [scheduleHide]);
  const handleFocusIn = useCallback(e => {
    if (!isEnabled) {
      return;
    }
    // Only show tooltip for keyboard focus (:focus-visible),
    // not programmatic focus (e.g. dialog auto-focus, touch tap)
    const target = e.target;
    if (!target.matches(':focus-visible')) {
      return;
    }
    clearTimeouts();
    layer.show();
  }, [isEnabled, clearTimeouts, layer]);
  const handleFocusOut = useCallback(() => {
    scheduleHide();
  }, [scheduleHide]);

  // Pressing the trigger hides its own tooltip: once the control is activated
  // the hint has served its purpose, and a tooltip lingering over a
  // just-pressed control reads as stale. Fires on pointerdown so it feels
  // immediate. Uncontrolled tooltips only — a controlled tooltip's visibility
  // is owned by the consumer. `layer.hide()` self-guards when already closed.
  const handlePointerDown = useCallback(() => {
    if (isOpen !== undefined) {
      return;
    }
    clearTimeouts();
    layer.hide();
  }, [isOpen, clearTimeouts, layer]);

  // Interaction ref that handles event listeners only
  const interactionRef = useCallback(el => {
    // Cleanup previous element
    if (triggerRef.current) {
      triggerRef.current.removeEventListener('mouseenter', handleMouseEnter);
      triggerRef.current.removeEventListener('mouseleave', handleMouseLeave);
      triggerRef.current.removeEventListener('focusin', handleFocusIn);
      triggerRef.current.removeEventListener('focusout', handleFocusOut);
      triggerRef.current.removeEventListener('pointerdown', handlePointerDown);
    }
    if (el) {
      // Attach hover listeners
      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('mouseleave', handleMouseLeave);
      // Press-to-dismiss: activating the trigger hides its own tooltip.
      el.addEventListener('pointerdown', handlePointerDown);

      // Attach focus listeners based on focusTrigger option
      const shouldAttachFocus = focusTrigger === 'always' || focusTrigger === 'auto' && isFocusable(el);
      if (shouldAttachFocus) {
        el.addEventListener('focusin', handleFocusIn);
        el.addEventListener('focusout', handleFocusOut);
      }
    }
    triggerRef.current = el;
  }, [focusTrigger, handleMouseEnter, handleMouseLeave, handleFocusIn, handleFocusOut, handlePointerDown]);

  // Combined ref - shorthand for calling both positionRef and interactionRef
  const ref = useCallback(el => {
    layer.ref(el);
    interactionRef(el);
  }, [layer, interactionRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);

  // Show on mount when isDefaultOpen is true
  useEffect(() => {
    if (isDefaultOpen) {
      layer.show();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only: isDefaultOpen is not reactive
  }, []);

  // Controlled open state — overrides hover/focus triggers
  useEffect(() => {
    if (isOpen === undefined) {
      return;
    }
    if (isOpen) {
      clearTimeouts();
      layer.show();
    } else {
      clearTimeouts();
      layer.hide();
    }
  }, [isOpen, clearTimeouts, layer]);

  // Dismiss on Escape (WCAG 1.4.13 — dismissible). Uncontrolled tooltips only;
  // a controlled tooltip's visibility is owned by the consumer. The listener is
  // mounted for the lifetime of an uncontrolled tooltip rather than gated on
  // `layer.isOpen` (React state, which can lag a frame behind the DOM) —
  // `layer.hide()` self-guards and no-ops when the layer is already closed.
  // Guarded against IME composition-cancel.
  useEffect(() => {
    if (isOpen !== undefined) {
      return;
    }
    const handleKeyDown = e => {
      if (e.key !== 'Escape') {
        return;
      }
      if (e.isComposing || e.keyCode === 229) {
        return;
      }
      clearTimeouts();
      layer.hide();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, layer, clearTimeouts]);

  // Render function that wraps layer.render with tooltip styling
  const renderTooltip = useCallback((children, props) => {
    const renderPlacement = props?.placement ?? placement;
    const renderProps = {
      placement: renderPlacement,
      alignment: props?.alignment ?? alignment,
      role: 'tooltip',
      xstyle: [popoverXstyle, layerAnimations[renderPlacement]],
      className: themeProps('tooltip').className,
      // Keep the tooltip open while the pointer is over the surface itself
      // (WCAG 1.4.13 hoverable). These sit on the layer container — the
      // element the user actually hovers — not the inner content div, since
      // mouseenter/leave do not bubble.
      onMouseEnter: cancelHide,
      onMouseLeave: scheduleHide
    };
    return layer.render(/*#__PURE__*/_jsx("div", {
      ...{
        className: "xfsso4q xy143xn x12gdq22 x1djylfy xw5ewwj x13faqbe"
      },
      children: children
    }), renderProps);
  }, [layer, placement, alignment, popoverXstyle, cancelHide, scheduleHide]);
  return {
    ref,
    positionRef: layer.ref,
    interactionRef,
    anchorId: layer.anchorId,
    describedBy: layer.id,
    renderTooltip
  };
}